import {
  IdeaMediaFile,
  IdeaStage,
} from "../@idea-planner/models/y-models/y-idea";
import { IdeaStatus } from "../@idea-planner/types";
import { AccountPlatformType } from "../shared/types/accounts";
import { GeneratedSkedApiClient } from "./generated-client";
import type { LinkPreviewResult } from "../@idea-planner/utils/link-preview.utils";
import type { paths } from "./generated/sked-api-openapi-types";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();

type CreateIdeaOperation = paths["/idea-planner/ideas/create"]["post"];
type UpdateIdeaOperation = paths["/idea-planner/ideas/{ideaId}"]["patch"];
type FetchIdeaActivitiesByIdsOperation =
  paths["/idea-planner/activities/by-ids"]["post"];
type AddIdeaActivityCommentOperation =
  paths["/idea-planner/activities/{activityId}/comments"]["post"];
type UpdateIdeaActivityCommentOperation =
  paths["/idea-planner/activities/{activityId}/comments/{commentId}"]["patch"];
type FetchIdeaLinkPreviewOperation =
  paths["/idea-planner/link-preview"]["post"];

type CreateIdeaRequestBody = NonNullable<
  CreateIdeaOperation["requestBody"]
>["content"]["application/json"];
type UpdateIdeaRequestBody = NonNullable<
  UpdateIdeaOperation["requestBody"]
>["content"]["application/json"];
type FetchIdeaActivitiesByIdsRequestBody = NonNullable<
  FetchIdeaActivitiesByIdsOperation["requestBody"]
>["content"]["application/json"];
type AddIdeaActivityCommentRequestBody = NonNullable<
  AddIdeaActivityCommentOperation["requestBody"]
>["content"]["application/json"];
type UpdateIdeaActivityCommentRequestBody = NonNullable<
  UpdateIdeaActivityCommentOperation["requestBody"]
>["content"]["application/json"];
type FetchIdeaLinkPreviewRequestBody = NonNullable<
  FetchIdeaLinkPreviewOperation["requestBody"]
>["content"]["application/json"];

export async function fetchIdeas({ groupIds }: { groupIds: string[] }) {
  const result = await generatedSkedApiClient.client.POST(
    "/idea-planner/ideas/by-group-ids",
    {
      body: {
        groupIds,
        status: "active",
      },
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as unknown as GetIdeasByGroupIdsResponse;
}

export async function createIdea(
  data: CreateIdeaRequestBody & {
    groupId: string;
    title: string;
    scheduledAt: string;
    platforms: AccountPlatformType[];
    smartCreateType?: "title-smart";
  }
) {
  const result = await generatedSkedApiClient.client.POST(
    "/idea-planner/ideas/create",
    {
      body: data,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as unknown as CreateIdeaResponse;
}

export async function updateIdea(data: {
  ideaId: string;
  scheduledAt?: string;
}) {
  const { ideaId, ...body } = data;
  const result = await generatedSkedApiClient.client.PATCH(
    "/idea-planner/ideas/{ideaId}",
    {
      params: {
        path: {
          ideaId,
        },
      },
      body: body as UpdateIdeaRequestBody,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as unknown as UpdateIdeaScheduledAtResponse;
}

export async function fetchIdeaLinkPreview(
  body: FetchIdeaLinkPreviewRequestBody,
  options?: { signal?: AbortSignal }
): Promise<LinkPreviewResult> {
  const result = await generatedSkedApiClient.client.POST(
    "/idea-planner/link-preview",
    {
      body,
      signal: options?.signal,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as unknown as LinkPreviewResult;
}

export async function fetchIdeaActivitiesByIds(
  body: FetchIdeaActivitiesByIdsRequestBody
): Promise<GetIdeaActivitiesByIdsResponse> {
  const result = await generatedSkedApiClient.client.POST(
    "/idea-planner/activities/by-ids",
    {
      body,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as GetIdeaActivitiesByIdsResponse;
}

export async function fetchIdeaActivity(
  activityId: string
): Promise<GetIdeaActivityByIdResponse> {
  const result = await generatedSkedApiClient.client.GET(
    "/idea-planner/activities/{activityId}",
    {
      params: {
        path: { activityId },
      },
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as GetIdeaActivityByIdResponse;
}

export async function addIdeaActivityComment({
  activityId,
  ...body
}: AddIdeaActivityCommentRequestBody & {
  activityId: string;
}): Promise<IdeaCommentMutationResponse> {
  const result = await generatedSkedApiClient.client.POST(
    "/idea-planner/activities/{activityId}/comments",
    {
      params: {
        path: { activityId },
      },
      body,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as IdeaCommentMutationResponse;
}

export async function updateIdeaActivityComment({
  activityId,
  commentId,
  ...body
}: UpdateIdeaActivityCommentRequestBody & {
  activityId: string;
  commentId: string;
}): Promise<IdeaCommentMutationResponse> {
  const result = await generatedSkedApiClient.client.PATCH(
    "/idea-planner/activities/{activityId}/comments/{commentId}",
    {
      params: {
        path: { activityId, commentId },
      },
      body,
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as IdeaCommentMutationResponse;
}

export async function deleteIdeaActivityComment({
  activityId,
  commentId,
}: {
  activityId: string;
  commentId: string;
}): Promise<IdeaCommentMutationResponse> {
  const result = await generatedSkedApiClient.client.DELETE(
    "/idea-planner/activities/{activityId}/comments/{commentId}",
    {
      params: {
        path: { activityId, commentId },
      },
    }
  );

  return (await generatedSkedApiClient.unwrap(
    result
  )) as IdeaCommentMutationResponse;
}

interface IdeaResponse {
  _id: string;
  groupId: string;
  status: IdeaStatus;
  data: IdeaData;
  createdAt: Date;
  updatedAt: Date;
}

interface GetIdeasByGroupIdsResponse {
  status: "ok";
  ideas: IdeaResponse[];
}

interface CreateIdeaResponse {
  status: "ok";
  idea: {
    _id: string;
    yDoc: string;
    data: IdeaData;
  };
  activity: {
    _id: string;
    yDoc: string;
    data: object;
  };
}

interface UpdateIdeaScheduledAtResponse {
  status: "ok";
  idea: {
    _id: string;
    yDoc: string;
    data: IdeaData;
  };
}

interface GetIdeaActivitiesByIdsResponse {
  status: "ok";
  activities: IdeaActivityResponse[];
}

interface GetIdeaActivityByIdResponse {
  status: "ok";
  activity: IdeaActivityResponse;
}

interface IdeaActivityResponse {
  _id: string;
  data: {
    _id: string;
    groupId: string;
    adminId: string;
    items: Array<Record<string, unknown>>;
  };
}

interface IdeaCommentMutationResponse {
  status: "ok";
  comment: {
    _id: string;
    type: "comment";
    createdAt: string;
    createdBy: string;
    content: string;
    isEdited?: boolean;
    isDeleted?: boolean;
    visibility?: "internal" | "external";
  };
  activity: {
    _id: string;
    yDoc: string;
    data: object;
  };
}

interface IdeaData {
  _id: string;
  adminId: string;
  groupId: string;
  status: IdeaStatus;
  title: string;
  caption: string;
  scheduledAt?: string;
  createdAt: string;
  createdBy: string;
  orderIndex?: string;
  lastDiscardedAt?: string;
  stage: IdeaStage;
  labels: string[];
  assignees: string[];
  mediaFiles: IdeaMediaFile[];
  platforms: string[];
  pinnedAt: string | undefined;
  signOff: boolean | null | undefined;
  readyToReview: boolean | null | undefined;
  topicDetails: string | undefined;
  hashTagsKeywords: string | undefined;
  creativeBrief: string | undefined;
  copyBrief: string | undefined;
  toneOfVoiceProfileId?: string | undefined;
}
